import { useData } from "../../runtime/provider";
import { uploadImage } from "@components/Account/Profile/profile.utils";
import { pickImage } from "@components/ImagePicker";
import LArtwork, { getDeterministicPlaceholder } from "@components/LArtwork";
import LBox from "@components/LBox";
import LIcon from "@components/LIcon";
import LInput from "@components/LInput";
import LPressable from "@components/LPressable";
import LText from "@components/LText";
import { CreatePlaylistModalProps } from "@components/modals/CreatePlaylistModal/types";
import PremiumButton from "@components/PremiumButton";
import TickBox from "@components/TickBox";
import { getActiveTab } from "@hooks/useActiveTab";
import { useCreatePlaylist } from "@hooks/trpc/usePlaylists";
import { theme } from "@theme";
import Alert from "@components/Alert";
import {
  BottomSheetSubmitButton,
  BottomSheetTopBarLayout,
} from "@utils/swmModal/components/BottomSheetPrimitives";
import AppModalSheet from "@utils/appModal/components/AppModalSheet";
import { router } from "expo-router";
import { forwardRef, useMemo, useRef, useState } from "react";
import { Keyboard, Platform, TextInput } from "react-native";
import {
  type SheetAdapterRef,
  useBottomSheetManager,
} from "react-native-bottom-sheet-stack";
import uuid from "react-native-uuid";

const IMAGE_SIZE = 150;

type CreatePlaylistModalSWMContentProps = NonNullable<CreatePlaylistModalProps>;

const CreatePlaylistModalSWM = ({
  songToAdd,
}: CreatePlaylistModalSWMContentProps) => {
  const session = useData();
  const [title, setTitle] = useState(songToAdd?.title || "");
  const [visibility, setVisibility] = useState(false);
  const [localImage, setLocalImage] = useState<{
    uri: string;
    mimeType?: string | null;
  }>();
  const { createPlaylist } = useCreatePlaylist();
  const bottomSheetManager = useBottomSheetManager();

  const id = useMemo(() => uuid.v4().toString(), []);
  const titleTextInputRef = useRef<TextInput>(null);
  const hasSelectedInitialTitleRef = useRef(false);

  const awaitKeyboardDismiss = async () => {
    if (Keyboard.isVisible()) {
      await new Promise((resolve) => {
        const listener = Keyboard.addListener("keyboardDidHide", () => {
          listener.remove();
          resolve(true);
        });
        Keyboard.dismiss();
      });
    }
  };

  const create = async () => {
    try {
      const trimmedTitle = title?.trim();
      if (!trimmedTitle || trimmedTitle.length === 0) {
        Alert.alert("Please enter a valid playlist name");
        return;
      }

      let imageUrl = undefined;

      if (localImage?.uri) {
        imageUrl = await uploadImage(session, {
          uri: localImage.uri,
          contentType: localImage.mimeType,
        });
      }

      createPlaylist({
        id,
        title: trimmedTitle,
        visibility: visibility ? "public" : "private",
        image: imageUrl,
        songToAdd,
      });

      await awaitKeyboardDismiss();
      await bottomSheetManager.closeAll();

      if (Platform.OS === "web") {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      router.push(`/(app)/(tabs)/${getActiveTab()}/playlist/${id}`);
    } catch (error) {
      console.error("Error creating playlist", error);
    }
  };

  const handlePressPickImage = async () => {
    await awaitKeyboardDismiss();

    await pickImage(
      (url) => {
        setLocalImage(url);
      },
      () => {},
    );
  };

  const isValid = title.length > 0;

  return (
    <BottomSheetTopBarLayout
      contentProps={{ pt: "s1" }}
      title="Create your playlist"
    >
      <LPressable
        onPress={handlePressPickImage}
        sentryEventName="pick_playlist_artwork_swm"
        style={{
          marginBottom: theme.spacing.s3,
          width: IMAGE_SIZE,
          alignSelf: "center",
        }}
      >
        <LBox
          alignSelf="center"
          borderRadius="l"
          overflow="hidden"
          width={IMAGE_SIZE}
        >
          <LArtwork
            size={IMAGE_SIZE}
            imageSize="medium"
            url={localImage?.uri ?? songToAdd?.albums.at(0)?.image}
            placeholderImage={getDeterministicPlaceholder(id)}
          />
          <LIcon
            name="brush"
            size={32}
            position="absolute"
            right={4}
            bottom={4}
          />
        </LBox>
      </LPressable>
      <LBox mb="s3">
        <LInput
          autoFocus={Platform.OS !== "web"}
          onChangeText={(text) => setTitle(text)}
          onFocus={() => {
            if (hasSelectedInitialTitleRef.current) {
              return;
            }

            hasSelectedInitialTitleRef.current = true;
            titleTextInputRef.current?.setSelection(0, title.length);
          }}
          placeholder="Your playlist name"
          placeholderTextColor={theme.colors.blackTranslucent50}
          ref={titleTextInputRef}
          style={{
            backgroundColor: theme.colors.white,
            borderColor: theme.colors.blackTranslucent10,
            color: theme.colors.black,
          }}
          value={title}
          variant="primary"
        />
      </LBox>
      <LBox mb="m2">
        <LPressable
          onPress={() => setVisibility((prev) => !prev)}
          sentryEventName="make_playlist_public_swm"
        >
          <LBox flexDirection="row">
            <TickBox
              on={!!visibility}
              title="make_playlist_public_swm"
              toggle={() => setVisibility((prev) => !prev)}
            />
            <LText color="black" variant="inputTitle">
              Make playlist public
            </LText>
          </LBox>
        </LPressable>
      </LBox>
      <PremiumButton sentryEventName="create_playlist_swm">
        <BottomSheetSubmitButton
          enabled={isValid}
          label="CREATE PLAYLIST"
          marginBottom="zero"
          onPress={create}
          sentryEventName="create_playlist_button_swm"
        />
      </PremiumButton>
    </BottomSheetTopBarLayout>
  );
};

export const CreatePlaylistModalSWMSheet = forwardRef<
  SheetAdapterRef,
  NonNullable<CreatePlaylistModalProps>
>(function CreatePlaylistModalSWMSheet({ songToAdd }, ref) {
  return (
    <AppModalSheet keyboardBehavior="inset" ref={ref} showHandle={false}>
      <CreatePlaylistModalSWM songToAdd={songToAdd} />
    </AppModalSheet>
  );
});

export default CreatePlaylistModalSWM;
